import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { useAppSelector as useSelector } from "../../../store";
import { TCandidateProfileStesps } from "../../../store/onboarding/types";
import Calendar from "../../UI/Icons/Calendar";

export const getIsStage3Activate = (
  profileSteps: TCandidateProfileStesps[]
) => {
  return profileSteps?.some(
    (step) => step.step_number === 3 && (step.action === "approved" || step.action === "activate")
  );
};

interface CandidateJobHeaderProps {
  jobListingCount?: number;
  pendingJobCount?: number;
  acceptedJobCount?: number;
  isCandidateFilledAllForms?: boolean;
  documentsExpired?: boolean;
}

const CandidateJobHeader: React.FC<CandidateJobHeaderProps> = ({
  jobListingCount,
  pendingJobCount,
  acceptedJobCount,
  isCandidateFilledAllForms,
  documentsExpired,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const profileSteps = useSelector(
    (state) => state.onboarding.candidateProfileSteps
  );
  const isStage3Activate = getIsStage3Activate(profileSteps);

  const handleActiveTab = (tableName: string) => {
    if (location.pathname.includes(tableName)) {
      return "bg-black text-white";
    } else {
      return "bg-off-white text-black";
    }
  };

  const handleTabNavigation = (tableName: string) => {
    if (isStage3Activate === true && !documentsExpired) {
      if (tableName === "listings") {
        navigate(AUTH_ROUTE.JOBS);
      } else if (tableName === "pending") {
        navigate(AUTH_ROUTE.PENDING_JOBS);
      } else if (tableName === "accepted") {
        navigate(AUTH_ROUTE.ACCEPTED_JOBS);
      } else if (tableName === "history") {
        navigate(AUTH_ROUTE.HISTORY_JOBS);
      }
    }
  };

  const handleTabCount = (tableName: string) => {
    if (tableName === "listings") {
      return jobListingCount;
    } else if (tableName === "pending") {
      return pendingJobCount;
    } else if (tableName === "accepted") {
      return acceptedJobCount;
    } else if (tableName === "history") {
      return "";
    }
  };

  /**
   * Navigates the user to the jobs calendar page.
   * This function uses the `navigate` method to redirect
   * to the route specified in `AUTH_ROUTE.JOBS_CALENDAR`.
   */
  const redirectToCalendar = () => {
    if (!isStage3Activate || documentsExpired) {
      return;
    }
    navigate(AUTH_ROUTE.JOBS_CALENDAR);
  };

  /**
   * Represents a list of tabs for the Candidate Job Header component.
   * Each tab contains a `pageName` used for routing or identification
   * and a `tabName` displayed as the tab's label.
   *
   * Tabs included:
   * - "Job Listings" (pageName: "listings")
   * - "Pending" (pageName: "pending")
   * - "Accepted" (pageName: "accepted")
   * - "History" (pageName: "history")
   */
  const candidateTabList = [
    {
      pageName: "listings",
      tabName: "Job Listings",
    },
    {
      pageName: "pending",
      tabName: "Pending",
    },
    {
      pageName: "accepted",
      tabName: "Accepted",
    },
    {
      pageName: "history",
      tabName: "History",
    },
  ];
  const [isMobile, setIsMobile] = useState(window.innerWidth < 991);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 991);
    }

    window.addEventListener("resize", handleResize);

    // Clean up listener on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Generates a list of tab components for the candidate job header.
   *
   * This function maps over the `candidateTabList` array and creates a set of
   * tab elements. Each tab displays a count and a name, and applies dynamic
   * styles and click behavior based on the active tab and navigation logic.
   *
   * @returns A JSX element containing the list of tabs.
   */
  const generateTabList = () => {
    return candidateTabList.map((tabData, pageName) => (
      <div
        className={`top-radius-10px ${
          isMobile ? "py-13px" : "py-5px"
        } px-15px d-flex gap-5 align-items-center text-nowrap ${
          (isStage3Activate || !documentsExpired) ? "cursor-pointer" : "cursor-not-allowed"
        } ${handleActiveTab(tabData?.pageName)}`}
        onClick={() => handleTabNavigation(tabData?.pageName)}
        key={tabData?.tabName}
      >
        <div className="fs-22 fs-mobile-16 fw-700">
          {(tabData?.pageName === "pending" ||
            tabData?.pageName === "accepted") &&
            handleTabCount(tabData?.pageName)}
        </div>
        <div className="fs-22 fs-mobile-16 fw-400">{tabData?.tabName}</div>
      </div>
    ));
  };

  const isActiveCalendarPage = location.pathname.includes("calendar");

  return (
    <>
      <div className="d-flex flex-column">
        <div className="d-flex gap-10 align-items-lg-center justify-content-between overflow-auto mobile-scroll-hide">
          <div className="d-flex gap-10 align-items-center">
            {generateTabList()}
          </div>
          <div
            className={`top-radius-10px py-5px px-15px d-flex gap-5 align-items-center text-nowrap ${
              (isStage3Activate || !documentsExpired) ? "cursor-pointer" : "cursor-not-allowed"
            } ${handleActiveTab("calendar")}`}
            onClick={redirectToCalendar}
          >
            <Calendar color={`${isActiveCalendarPage ? "white" : "black"}`} />
            <div className="fs-22 fs-mobile-16 fw-400">My Calendar</div>
          </div>
        </div>
        <div className="bg-black w-100 h-5px"></div>
      </div>
    </>
  );
};

export default CandidateJobHeader;
